import React from 'react';
import { Box, Typography } from '@mui/material';
import { Icon, IconSize, type IconProps } from '@shared/icons';
import { APP_CONFIG } from '../../../../config/app.config';

type ChipTone = 'success' | 'warning' | 'error' | 'primary' | 'secondary' | 'neutral';

interface DashboardStatusChipProps {
  label: string;
  tone?: ChipTone;
  icon?: IconProps['icon'];
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}

const toneColor = (tone: ChipTone): string => {
  const colors = APP_CONFIG.ui.theme.colors;
  switch (tone) {
    case 'success':
      return colors.success;
    case 'warning':
      return colors.warning;
    case 'error':
      return colors.error;
    case 'primary':
      return colors.primary;
    case 'secondary':
      return colors.secondary;
    default:
      return colors.secondaryText;
  }
};

const DashboardStatusChip: React.FC<DashboardStatusChipProps> = ({
  label,
  tone = 'success',
  icon,
  onClick,
  disabled,
}) => {
  const color = toneColor(tone);
  const clickable = Boolean(onClick) && !disabled;

  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      disabled={clickable ? disabled : undefined}
      onClick={onClick}
      aria-label={clickable ? `Change status: ${label}` : undefined}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '100px',
        border: 'none',
        background: 'none',
        font: 'inherit',
        px: 1,
        py: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: clickable ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        ...(clickable && {
          '&:hover': {
            filter: 'brightness(0.97)',
          },
        }),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: color,
          opacity: 0.1,
        }}
      />
      {icon && (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            color,
            lineHeight: 0,
          }}
        >
          <Icon icon={icon} size={IconSize.SMALL} color={color} />
        </Box>
      )}
      <Typography
        sx={{
          position: 'relative',
          fontWeight: 500,
          fontSize: 12,
          lineHeight: '16px',
          letterSpacing: '0.5px',
          color,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default DashboardStatusChip;
